import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useLoginMutation } from "api";
import { useDispatch, useSelector } from "react-redux";
import { login } from "slices/auth";
import { Navigate, useNavigate } from "react-router-dom";
import { RootState } from "store";
import {
  Input,
  Button,
  FormGroup,
  Label,
  InlineLink,
  BackButton,
} from "@mwi/ui";
import useErrorHandler from "hooks/useErrorHandler";
import useTitle from "hooks/useTitle";
import LoginSideImage from "assets/images/login-side.jpg";
import { setPractice } from "slices/practice";

type Inputs = {
  password: string;
};

const LoginPage = () => {
  useTitle("Login");

  const dispatch = useDispatch();
  const errorHandler = useErrorHandler();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<Inputs>();

  const [submitLogin, { data: loginResult, isLoading, error }] =
    useLoginMutation();

  const { email } = useSelector((state: RootState) => ({
    email: state.auth.email,
  }));

  useEffect(() => {
    if (error) {
      errorHandler(error, setError);
    }
  }, [error]);

  useEffect(() => {
    if (loginResult) {
      dispatch(login(loginResult.user));
      dispatch(setPractice(loginResult.practice));
      navigate("/admin");
    }
  }, [dispatch, loginResult]);

  const triggerLogin = (data: Inputs) => {
    submitLogin({
      ...data,
      email: email!,
    });
  };

  if (!email) {
    return <Navigate to="/" replace />;
  }

  return (
    <>
      <div className="desktop:pl-[30%] relative">
        <BackButton to="/" replace className="absolute z-10" />
      </div>

      <form
        onSubmit={handleSubmit(triggerLogin)}
        className="flex flex-col h-full pt-10 tablet:pt-1"
        noValidate
      >
        <div className="hidden desktop:block fixed inset-y-0 left-0 right-[70%]">
          <img
            src={LoginSideImage}
            alt=""
            className="object-cover w-full h-full"
          />
        </div>

        <div className="desktop:fixed inset-y-0 right-0 left-[30%] flex-grow">
          <div className="w-full h-full mx-auto max-w-[37.5rem]">
            <div className="flex flex-col justify-center h-full tablet:-mt-16">
              <h1 className="mt-16 text-4xl font-bold tablet:text-6xl">
                Welcome Back
              </h1>

              <p className="hidden mt-8 text-lg tablet:block">
                Enter your password to login
              </p>

              <FormGroup
                className="!mt-10 tablet:!mt-12"
                error={errors.password?.message}
              >
                <Label>Password</Label>
                <Input
                  type="password"
                  {...register("password")}
                  autoComplete="off"
                />
              </FormGroup>

              <InlineLink to="/forgot" size="medium">
                Forgot password?
              </InlineLink>

              <div className="pt-16 mt-auto tablet:mt-0 tablet:max-w-xs">
                <Button
                  block
                  type="submit"
                  loading={isLoading}
                  className="!px-[60px] !py-[18px] w-full tablet:w-max"
                >
                  Continue
                </Button>
              </div>
            </div>
          </div>
        </div>
      </form>
    </>
  );
};

export default LoginPage;
